import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import {
  notificationsQueue,
  useNotificationsQueue,
} from '#src/hooks/useNotificationsQueue.ts';
import { snackNotificationLevelFromJSON } from '#src/protos/webview/channel/native_messages.ts';

import { SnackNotification as Notification } from './SnackNotification/index.tsx';
import type { NotificationValues, SnackNotificationOptions } from './index.ts';
import {
  SnackNotificationContext,
  notifications,
  useSnackNotificationContext,
} from './index.ts';

type Props = {
  children: ReactNode;
};

const formatValues = (values: NotificationValues = {}): NotificationValues =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join('\n') : value,
    ])
  );

export const SnackNotificationProvider = ({ children }: Props) => {
  const notificationQueue = useNotificationsQueue();
  const { formatMessage } = useIntl();
  const [notification, setNotification] =
    useState<SnackNotificationContext['notification']>(null);
  const { isFlutterApp, postMessage } = useIsMobileApp();

  useEffect(() => {
    // Need to do that in a useEffect because useExternalSyncStore can't be called in the render function
    if (notificationQueue.length && !notification) {
      const next = notificationsQueue.popNotification();
      // oxlint-disable-next-line react/set-state-in-effect
      setNotification(next);
    }
  }, [notificationQueue, notification]);

  const showNotification = useCallback(
    (
      id: keyof typeof notifications,
      values: NotificationValues = {},
      opts?: SnackNotificationOptions
    ) => {
      if (isFlutterApp) {
        const message = formatMessage(notifications[id], formatValues(values));
        // formatMessage is not always returning a string even if typed as it.
        if (typeof message === 'string') {
          postMessage('displaySnackNotification', {
            message,
            level: snackNotificationLevelFromJSON(opts?.level || 'INFO'),
            url: opts?.link,
          });
          return;
        }
      }

      setNotification({
        id,
        values,
        options: opts,
      });
    },
    [formatMessage, isFlutterApp, postMessage]
  );

  const value = useMemo(
    () => ({
      showNotification,
      notification,
      clearNotification: () => {
        setNotification(null);
      },
    }),
    [showNotification, notification, setNotification]
  );

  return (
    <SnackNotificationContext value={value}>
      {children}
    </SnackNotificationContext>
  );
};

export const SnackNotification = () => {
  const { formatMessage } = useIntl();
  const { notification, clearNotification } = useSnackNotificationContext();
  const { onClosed, ...options } = notification?.options || {};
  const onClose = useCallback(() => {
    clearNotification();
    if (onClosed) {
      onClosed();
    }
  }, [onClosed, clearNotification]);

  return (
    <Notification
      notification={
        notification
          ? formatMessage(
              notifications[notification.id],
              formatValues(notification.values)
            )
          : null
      }
      opts={options}
      onClose={onClose}
    />
  );
};
